// Core modules
import { Injectable } from '@angular/core';

// Application services
import { environment } from '../../environments/environment';

/**
 * Thin wrapper around the AWS CloudWatch RUM web client (aws-rum-web).
 *
 * Loaded lazily and guarded end-to-end so a RUM misconfiguration (missing
 * app monitor id, network failure fetching Cognito credentials, etc.) can
 * never break the app - every public method is a safe no-op until init()
 * has successfully created the underlying client.
 *
 * See customer-portal/terraform/environments/{production,eu-production} for
 * how the appMonitorId/identityPoolId values in each environment.*.ts are
 * provisioned.
 */
@Injectable()
export class RumService {
  private client: any;

  /**
   * Click tracking selector for RUM's 'interaction' telemetry - broad on purpose
   * (buttons, links, Angular Material interactive components) to capture
   * effectively all meaningful clicks. Tune based on real data in the RUM console.
   */
  private static readonly CLICK_SELECTOR = [
    'button', 'a', '[role="button"]',
    'input[type="submit"]', 'input[type="button"]',
    '[mat-button]', '[mat-raised-button]', '[mat-flat-button]', '[mat-stroked-button]',
    '[mat-icon-button]', '[mat-fab]', '[mat-mini-fab]',
    'mat-checkbox', 'mat-radio-button', 'mat-slide-toggle',
    '[routerLink]'
  ].join(', ');

  /**
   * Dynamically loads and initializes the RUM web client. No-ops when disabled
   * for the current environment (dev/staging) or if already initialized.
   */
  async init(): Promise<void> {
    if (!environment.rum?.enabled || this.client) { return; }

    try {
      const { AwsRum } = await import('aws-rum-web');
      const { appMonitorId, identityPoolId, region } = environment.rum;

      this.client = new AwsRum(appMonitorId, '1.0.0', region, {
        sessionSampleRate: 1,
        identityPoolId,
        endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
        allowCookies: true,
        enableXRay: false,
        // Angular's hash-based routing (useHash: true) makes automatic pushState-based
        // page-view detection unreliable (location.pathname never changes) - page views
        // are instead recorded explicitly from the Router's NavigationEnd, see
        // app.component.ts.
        disableAutoPageView: true,
        telemetries: [
          'errors',
          'performance',
          'http',
          ['interaction', { events: [{ event: 'click', cssLocator: RumService.CLICK_SELECTOR }] }]
        ]
      });
    } catch (error) {
      // Never let a RUM misconfiguration break the app.
      console.error('Failed to initialize AWS RUM', error);
    }
  }

  /** Records a page view. Call on every route change (see app.component.ts). */
  recordPageView(path: string): void {
    this.client?.recordPageView(path);
  }

  /** Attaches attributes (e.g. clientName, roleName) to the current session for filtering in the RUM console. */
  identify(attributes: Record<string, string>): void {
    this.client?.addSessionAttributes(attributes);
  }

  /**
   * Records a custom, named business event - the RUM equivalent of a Google Analytics custom event.
   * Requires CustomEvents to be enabled on the app monitor (see terraform/modules/rum-monitor), otherwise
   * events are silently dropped server-side. Searchable by `type` in the RUM console's Events tab.
   */
  recordEvent(type: string, data: Record<string, any>): void {
    this.client?.recordEvent(type, data);
  }
}
